import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    // Stop post-load section hydration from rewriting this menu. That morph clears
    // aria-expanded mid-interaction and makes hover/click stop until reopen.
    this.removeAttribute('data-hydration-key');

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    document.addEventListener('click', this.#onDocumentClick);
    this.addEventListener('focusout', this.#onFocusOut, true);
    this.addEventListener('keydown', this.#onEscape);
    this.addEventListener('pointerover', this.#onCascadePointerOver);
    this.addEventListener('focusin', this.#onCascadeFocusIn);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.removeEventListener('click', this.#onDocumentClick);
    this.removeEventListener('focusout', this.#onFocusOut, true);
    this.removeEventListener('keydown', this.#onEscape);
    this.removeEventListener('pointerover', this.#onCascadePointerOver);
    this.removeEventListener('focusin', this.#onCascadeFocusIn);
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);


  /**
   * @param {Event} event
   */
  #onDocumentClick = (event) => {
    if (!this.#state.activeItem) return;

    const target =
      typeof event.composedPath === 'function'
        ? event.composedPath()[0]
        : /** @type {EventTarget | undefined} */ (event.target);

    if (target instanceof Node && this.#pointerEventInside(target)) return;

    this.#deactivate();
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onEscape = (event) => {
    if (event.key !== 'Escape' || !this.#state.activeItem) return;

    event.stopPropagation();
    this.#deactivate();
  };

  /**
   * Close when focus leaves the menu (Tab out). `li` focus/blur is not used—`focus` fires before `click` in capture
   * and was opening then immediately toggling closed.
   * @param {FocusEvent} event
   */
  #onFocusOut = (event) => {
    if (!this.#state.activeItem) return;

    const next = event.relatedTarget;

    if (next instanceof Node && this.#pointerEventInside(next)) return;

    this.#deactivate();
  };

  /**
   * @param {Node} node
   */
  #nodeIsInsideMenu(node) {
    return node instanceof Element && typeof this.contains === 'function' && this.contains(node);
  }

  /**
   * @param {Node} node
   * @returns {boolean}
   */
  #pointerEventInside(node) {
    if (!(node instanceof Node)) return false;

    if (this.#nodeIsInsideMenu(node)) {
      return true;
    }

    const overflowHost = this.refs.overflowMenu;
    const overflowPane =
      overflowHost instanceof HTMLElement
        ? /** @type {HTMLElement | null} */ (
            overflowHost.shadowRoot?.querySelector('[part="overflow"]')
          )
        : null;

    return Boolean(overflowPane?.contains(node));
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Click / tap toggles mega menu flyouts (`li` focus handlers are not used—they run before click and duplicated open/close).
   * @param {Event} event
   */
  handleMenuItemClick = (event) => {
    const target =
      typeof event.composedPath === 'function'
        ? event.composedPath()[0]
        : /** @type {EventTarget | null} */ (event.target);

    if (!(target instanceof Element)) return;
    if (target.closest('.menu-list__submenu')) return;

    const listItem = target.closest('.menu-list__list-item');
    if (!(listItem instanceof HTMLElement) || !this.contains(listItem)) return;

    const item = findMenuItem(listItem);
    if (!(item instanceof HTMLElement)) return;

    if (!menuItemOpensFlyout(listItem, item)) return;

    event.preventDefault();
    event.stopPropagation();

    if (item === this.#state.activeItem) {
      this.#deactivate(item);
      return;
    }

    this.activate(event, item);
  };

  /**
   * Keyboard parity for triggers (Enter / Space).
   * @param {KeyboardEvent} event
   */
  handleMenuItemKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    const raw =
      typeof event.composedPath === 'function'
        ? event.composedPath()[0]
        : /** @type {EventTarget | null} */ (event.target);

    if (!(raw instanceof Element)) return;
    if (raw.closest('.menu-list__submenu')) return;

    const listItem = raw.closest('.menu-list__list-item');

    if (!(listItem instanceof HTMLElement) || !this.contains(listItem)) return;

    const item = findMenuItem(listItem);

    if (!(item instanceof HTMLElement)) return;

    if (!menuItemOpensFlyout(listItem, item)) return;

    event.preventDefault();
    event.stopPropagation();

    if (item === this.#state.activeItem) {
      this.#deactivate(item);
      return;
    }

    this.activate(event, item);
  };

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent | Event} event
   * @param {HTMLElement | null} [explicitItem]
   */
  activate = (event, explicitItem = null) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!this.headerComponent) return;

    const item =
      explicitItem instanceof HTMLElement
        ? explicitItem
        : event.target instanceof Element
          ? findMenuItem(event.target)
          : null;

    if (!(item instanceof HTMLElement) || item === this.#state.activeItem) return;

    const menuListItem = /** @type {HTMLElement | null} */ (item.closest('.menu-list__list-item'));
    const isDefaultSlot = !(menuListItem instanceof HTMLElement && menuListItem.slot);

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';

      const prevSubmenu = findSubmenu(previouslyActiveItem);
      if (prevSubmenu) {
        delete prevSubmenu.dataset.active;
        this.#resetCascadeMenus(prevSubmenu);
      }
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
      this.#resetCascadeMenus(submenu);
    }
  };

  /**
   * Clear cascade parent / children selection so nothing is open until hover.
   * @param {ParentNode} [root]
   */
  #resetCascadeMenus = (root = this) => {
    const cascades =
      root instanceof Element && root.matches('[data-mega-menu-cascade]')
        ? [root]
        : root.querySelectorAll('[data-mega-menu-cascade]');

    cascades.forEach((cascade) => {
      if (cascade instanceof HTMLElement) {
        this.#clearCascadeSelection(cascade);
      }
    });
  };

  /**
   * Hide the children panel and clear active / dimmed parent state.
   * @param {HTMLElement} cascade
   */
  #clearCascadeSelection = (cascade) => {
    if (!cascade.dataset.activeIndex && cascade.dataset.cascadeInteracted == null) return;

    delete cascade.dataset.activeIndex;
    delete cascade.dataset.cascadeInteracted;

    cascade.querySelectorAll('[data-cascade-parent]').forEach((item) => {
      item.classList.remove('mega-menu__cascade-item--active');
      const trigger = item.querySelector('.mega-menu__cascade-link--parent');
      if (trigger instanceof HTMLElement) {
        trigger.removeAttribute('aria-current');
      }
    });

    cascade.querySelectorAll('[data-cascade-children]').forEach((list) => {
      list.classList.remove('is-active');
    });

    this.#refreshOpenSubmenuHeight();
  };

  /**
   * Keep children open only while hovering parents or the open children column.
   * Clear when moving to featured / promo / other menu chrome.
   * @param {Element} target
   */
  #syncCascadeHoverTarget = (target) => {
    const cascade = target.closest('[data-mega-menu-cascade]');

    // Outside the cascade but still in the open mega menu — hide children again.
    if (!(cascade instanceof HTMLElement)) {
      const submenu = target.closest('.menu-list__submenu');
      if (submenu) this.#resetCascadeMenus(submenu);
      return;
    }

    const parentLink = target.closest('.mega-menu__cascade-link--parent');
    const parentItem =
      parentLink instanceof HTMLElement
        ? parentLink.closest('[data-cascade-parent]')
        : null;
    if (parentItem instanceof HTMLElement && cascade.contains(parentItem)) {
      this.#activateCascadeParent(parentItem);
      return;
    }

    // Gaps between parent rows, or the children column — keep the current selection.
    if (target.closest('.mega-menu__cascade-parents, .mega-menu__cascade-children')) {
      return;
    }

    // Featured / promo columns — close the right-hand panel.
    if (target.closest('.mega-menu__cascade-featured, .mega-menu__cascade-promo')) {
      this.#clearCascadeSelection(cascade);
      return;
    }

    // Column gaps inside the cascade grid: keep selection so the pointer can
    // travel from parents → children without clearing mid-gap.
  };

  /**
   * Reveal cascade children when hovering a middle-column parent.
   * @param {PointerEvent} event
   */
  #onCascadePointerOver = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    this.#syncCascadeHoverTarget(target);
  };

  /**
   * Keyboard parity for cascade parents (focus moves into a parent link).
   * @param {FocusEvent} event
   */
  #onCascadeFocusIn = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    this.#syncCascadeHoverTarget(target);
  };

  /**
   * @param {HTMLElement} parentItem
   */
  #activateCascadeParent = (parentItem) => {
    const cascade = parentItem.closest('[data-mega-menu-cascade]');
    if (!(cascade instanceof HTMLElement)) return;

    const index = parentItem.dataset.cascadeIndex;
    if (index == null) return;

    const hasChildren = !parentItem.hasAttribute('data-cascade-empty');

    // Dim inactive middle items once a parent is hovered.
    cascade.dataset.cascadeInteracted = 'true';

    const alreadyActive = cascade.dataset.activeIndex === index;
    cascade.dataset.activeIndex = index;

    // Always refresh classes — don't return early on index match alone.
    cascade.querySelectorAll('[data-cascade-parent]').forEach((item) => {
      const isActive = item === parentItem;
      item.classList.toggle('mega-menu__cascade-item--active', isActive);

      const trigger = item.querySelector('.mega-menu__cascade-link--parent');
      if (trigger instanceof HTMLElement) {
        // Do not use aria-expanded here — theme CSS opens any submenu whose
        // list-item :has([aria-expanded='true']), which stacked every panel.
        if (isActive) {
          trigger.setAttribute('aria-current', 'true');
        } else {
          trigger.removeAttribute('aria-current');
        }
      }
    });

    cascade.querySelectorAll('[data-cascade-children]').forEach((list) => {
      if (!(list instanceof HTMLElement)) return;

      const isActive = hasChildren && list.dataset.cascadeIndex === index;
      list.classList.toggle('is-active', isActive);
    });

    if (!alreadyActive) {
      this.#refreshOpenSubmenuHeight();
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }

  /**
   * Recalculate open submenu height after cascade children change.
   */
  #refreshOpenSubmenuHeight = () => {
    const item = this.#state.activeItem;
    if (!(item instanceof HTMLElement) || !this.headerComponent) return;

    const submenu = findSubmenu(item);
    if (!(submenu instanceof HTMLElement)) return;

    requestAnimationFrame(() => {
      const height = submenu.offsetHeight || 0;
      const next = `${height}px`;
      if (this.headerComponent?.style.getPropertyValue('--submenu-height') === next) return;
      this.headerComponent?.style.setProperty('--submenu-height', next);
      this.#setFullOpenHeaderHeight(height);
    });
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Top-level items that open the mega / overflow panel (click or keyboard).
 * @param {Element} listItem
 * @param {Element} item
 * @returns {boolean}
 */
function menuItemOpensFlyout(listItem, item) {
  if (!(listItem instanceof HTMLElement) || !(item instanceof HTMLElement)) return false;

  const submenuEl = findSubmenu(item);

  return (
    listItem.slot === 'more' ||
    item.matches('[aria-haspopup="true"]') ||
    submenuEl instanceof HTMLElement
  );
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element.matches('[slot="more"]')) {
    // Select the first overflowing menu item when activating the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  if (element.matches('[ref="menuitem"]')) {
    return /** @type {HTMLElement} */ (element);
  }

  return element.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
